import React, { FC, ReactElement, Ref, useRef } from 'react';

import { MessageType } from '@savgroup-front-common/types';

import { SafeFormattedMessage } from '../../../../formatters';
import { useCombinedRefs } from '../../../../hooks';
import { useMatchWithQuery } from '../../../../hooks/useMatchWithQuery';

import {
  $ComplexSwitchContainer,
  $ComplexSwitchLeftButton,
  $ComplexSwitchRightButton,
} from './ComplexSwitch.styles';

export interface ComplexSwitchSide {
  label?: MessageType | string;
  path?: string;
  url?: string;
  onClick?: () => void;
  isActive?: boolean;
}

export interface ComplexSwitchProps {
  leftSide?: ComplexSwitchSide;
  rightSide?: ComplexSwitchSide;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  small?: boolean;
}

interface ComplexSwitchPropsRef extends ComplexSwitchProps {
  forwardedRef: Ref<HTMLInputElement>;
}

const ComplexSwitch: FC<ComplexSwitchPropsRef> = ({
  leftSide,
  forwardedRef,
  rightSide,
  leftIcon,
  rightIcon,
  small,
}) => {
  const complexSwitchRef = useRef<HTMLInputElement | null>(null);
  const combinedRefs = useCombinedRefs(forwardedRef, complexSwitchRef);

  const isLeftSideRouteActive = useMatchWithQuery(leftSide?.url ?? '');
  const isRightSideRouteActive = useMatchWithQuery(rightSide?.url ?? '');

  const isLeftSideActive = leftSide?.onClick
    ? !!leftSide.isActive
    : isLeftSideRouteActive;
  const isRightSideActive = rightSide?.onClick
    ? !!rightSide.isActive
    : isRightSideRouteActive;

  const handleSideClick =
    (side?: ComplexSwitchSide) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (side?.onClick) {
        event.preventDefault();
        side.onClick();
      }
    };

  return (
    <$ComplexSwitchContainer ref={combinedRefs}>
      {leftSide?.label && (leftSide.url || leftSide.onClick) && (
        <$ComplexSwitchLeftButton
          primary={isLeftSideActive}
          naked
          icon={leftIcon}
          href={leftSide.url}
          onClick={handleSideClick(leftSide)}
          $isActive={isLeftSideActive}
          small={small}
        >
          {SafeFormattedMessage(leftSide.label)}
        </$ComplexSwitchLeftButton>
      )}
      {rightSide?.label && (rightSide.url || rightSide.onClick) && (
        <$ComplexSwitchRightButton
          icon={rightIcon}
          primary={isRightSideActive}
          naked
          href={rightSide.url}
          onClick={handleSideClick(rightSide)}
          $isActive={isRightSideActive}
          small={small}
        >
          {SafeFormattedMessage(rightSide.label)}
        </$ComplexSwitchRightButton>
      )}
    </$ComplexSwitchContainer>
  );
};

export default React.forwardRef<HTMLInputElement, ComplexSwitchProps>(
  (props, ref) => <ComplexSwitch forwardedRef={ref} {...props} />,
);
